import { useCallback, useEffect, useMemo, useState } from 'react';

import { ValidationError } from 'yup';

import validateInteraction, {
  InteractionValidationSchema,
} from 'lane-shared/renderers/v5/validateInteraction';
import { ContentType } from 'lane-shared/types/content/Content';
import { SecurityModesEnum, SecurityRuleTypeEnum } from 'application-types';
import { FeatureFlag } from 'constants-flags';
import useFlag from 'lane-shared/hooks/useFlag';

type UpdateSession = {
  editingInteraction: any;
  setInteractionUpdate: (props: any) => void;
  updateInteraction: () => Promise<void>;
  loading: boolean;
};

/**
 * Deliberately narrower than the create session's submitInteraction, which
 * takes submit options and resolves to the created interaction. An edit
 * submits through the update mutation: it ignores options and resolves to
 * nothing, so a caller must not read a result from it.
 */
type EditSubmitInteraction = () => Promise<void>;

/**
 * Mirrors the shape useCreateInteraction returns so a host page can select one
 * session or the other at a single point.
 *
 * Validation is duplicated here rather than reused because useCreateInteraction
 * validates its own internal interaction, which in edit mode is a blank one.
 */
export default function useEditInteractionSession({
  content,
  isEditMode,
  updateSession,
}: {
  content: ContentType | undefined;
  isEditMode: boolean;
  updateSession: UpdateSession;
}) {
  const [validation, setValidation] = useState<ValidationError | null>(null);
  const [preventSubmission, setPreventSubmission] = useState(true);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [additionalSchemas, setAdditionalSchemas] = useState<
    Record<string, InteractionValidationSchema>
  >({});

  const interaction = updateSession.editingInteraction;

  const isFixDateTimeRangeValidationEnabled = Boolean(
    useFlag(FeatureFlag.FixDateTimeRangeValidation, false)
  );
  const isRestrictWebsiteUrlSchemesEnabled = Boolean(
    useFlag(FeatureFlag.RestrictWebsiteUrlSchemes, false)
  );

  // Mirrors useCreateInteraction's security level so an edit validates on the
  // same rules the member saw when the booking was created. Create rather than
  // Update because Update skips required validators outright.
  const security = useMemo(
    () => ({
      mode: SecurityModesEnum.Create,
      type: SecurityRuleTypeEnum.Creator,
      validationFlags: {
        [FeatureFlag.FixDateTimeRangeValidation]:
          isFixDateTimeRangeValidationEnabled,
        [FeatureFlag.RestrictWebsiteUrlSchemes]:
          isRestrictWebsiteUrlSchemesEnabled,
      },
    }),
    [isFixDateTimeRangeValidationEnabled, isRestrictWebsiteUrlSchemesEnabled]
  );

  const interactionKey = JSON.stringify(interaction);

  const validate = useCallback(
    async (shouldThrow: boolean = false) => {
      if (!content) {
        return;
      }

      try {
        await validateInteraction({ content, interaction, security });

        for (const schema of Object.values(additionalSchemas)) {
          schema.validateSync(interaction, { abortEarly: false });
        }

        setValidation(null);
        setPreventSubmission(false);

        return true;
      } catch (err: any) {
        setValidation(err);
        setPreventSubmission(true);

        if (shouldThrow) {
          throw err;
        }

        return false;
      }
    },
    [content, interaction, additionalSchemas, security]
  );

  useEffect(() => {
    if (isEditMode) {
      validate();
    }
    // validate is intentionally omitted: the edited interaction is a fresh
    // object every render, so depending on it would re-validate on every
    // render of the page every booking goes through. interactionKey changes
    // exactly when the interaction's validated content does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, content, interactionKey, additionalSchemas, security]);

  const validateAdditional = useCallback(
    (schema: InteractionValidationSchema | null, key: string = 'default') => {
      setAdditionalSchemas(prev => {
        if (schema === null) {
          if (!(key in prev)) {
            return prev;
          }

          const next = { ...prev };

          delete next[key];

          return next;
        }

        if (prev[key] === schema) {
          return prev;
        }

        return { ...prev, [key]: schema };
      });
    },
    []
  );

  return {
    interaction,
    loading: updateSession.loading,
    submitting: updateSession.loading,
    validation,
    submitAttempted,
    preventSubmission,
    updateInteraction: updateSession.setInteractionUpdate,
    submitInteraction: updateSession.updateInteraction as EditSubmitInteraction,
    validate,
    setInteractionUser: () => {},
    setSubmitAttempted,
    validateAdditional,
  } as const;
}
